import React, { useState, useEffect } from 'react';
import FormTemplateService, { FormFieldElement, FormTemplateDefinition } from '../../../services/FormTemplateService';
import { CLINICAL_FORM_TEMPLATES, ClinicalFormTemplate } from '../../../data/clinicalFormTemplates';

type ViewMode = 'library' | 'preview';
type CategoryFilter = 'all' | 'intake' | 'assessment' | 'consent' | 'treatment-plan' | 'hr' | 'measures';

const CATEGORY_LABELS: Record<string, string> = {
  'intake': 'Intake',
  'assessment': 'Assessment',
  'consent': 'Consent',
  'treatment-plan': 'Treatment Plan',
  'hr': 'HR / Onboarding',
  'measures': 'Measures',
};

const CATEGORY_COLORS: Record<string, string> = {
  'intake': '#4FC3F7',
  'assessment': '#81C784',
  'consent': '#FFB74D',
  'treatment-plan': '#CE93D8',
  'hr': '#F06292',
  'measures': '#64B5F6',
};

const Documentation: React.FC = () => {
  const formTemplateService = FormTemplateService.getInstance();
  const [viewMode, setViewMode] = useState<ViewMode>('library');
  const [selectedTemplate, setSelectedTemplate] = useState<ClinicalFormTemplate | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [installedTemplates, setInstalledTemplates] = useState<Set<string>>(new Set());
  const [showInstallSuccess, setShowInstallSuccess] = useState<string | null>(null);

  useEffect(() => {
    refreshInstalledStatus();
    const listener = () => refreshInstalledStatus();
    formTemplateService.subscribe(listener);
    return () => formTemplateService.unsubscribe(listener);
  }, []);

  const refreshInstalledStatus = async () => {
    const existing = await formTemplateService.getAllFormTemplates();
    const names = new Set(existing.map(t => t.name));
    setInstalledTemplates(names);
  };

  const handleInstall = async (template: ClinicalFormTemplate) => {
    await formTemplateService.saveFormTemplate(
      template.name,
      template.elements,
      template.category,
      false,
      template.description
    );
    await refreshInstalledStatus();
    setShowInstallSuccess(template.name);
    setTimeout(() => setShowInstallSuccess(null), 3000);
  };

  const handleInstallAll = async () => {
    for (const template of CLINICAL_FORM_TEMPLATES) {
      if (!installedTemplates.has(template.name)) {
        await formTemplateService.saveFormTemplate(
          template.name,
          template.elements,
          template.category,
          false,
          template.description
        );
      }
    }
    await refreshInstalledStatus();
    setShowInstallSuccess('All Phase 1 templates');
    setTimeout(() => setShowInstallSuccess(null), 3000);
  };

  const handlePreview = (template: ClinicalFormTemplate) => {
    setSelectedTemplate(template);
    setViewMode('preview');
  };

  const handleBackToLibrary = () => {
    setSelectedTemplate(null);
    setViewMode('library');
  };

  const filteredTemplates = CLINICAL_FORM_TEMPLATES.filter(t => {
    if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.tags.some(tag => tag.toLowerCase().includes(q))
      );
    }
    return true;
  });

  const installedCount = CLINICAL_FORM_TEMPLATES.filter(t => installedTemplates.has(t.name)).length;

  if (viewMode === 'preview' && selectedTemplate) {
    return (
      <div style={{ padding: '32px' }}>
        {/* Back button */}
        <button
          onClick={handleBackToLibrary}
          style={{
            background: 'none',
            border: 'none',
            color: '#87CEEB',
            cursor: 'pointer',
            fontSize: '14px',
            padding: '0',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          ← Back to Document Library
        </button>

        {/* Template header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#FFFFFF', margin: '0 0 8px 0' }}>
              {selectedTemplate.name}
            </h1>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{
                backgroundColor: CATEGORY_COLORS[selectedTemplate.category] || '#6CA6C1',
                color: '#000',
                padding: '2px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 600,
              }}>
                {CATEGORY_LABELS[selectedTemplate.category]}
              </span>
              {selectedTemplate.medicaidRequired && (
                <span style={{
                  backgroundColor: '#FF7043',
                  color: '#FFF',
                  padding: '2px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 600,
                }}>
                  Medicaid Required
                </span>
              )}
              <span style={{ color: '#87CEEB', fontSize: '13px' }}>
                {selectedTemplate.elements.length} fields
              </span>
            </div>
            <p style={{ color: '#B0BEC5', fontSize: '14px', marginTop: '8px', maxWidth: '700px' }}>
              {selectedTemplate.description}
            </p>
          </div>
          <button
            onClick={() => handleInstall(selectedTemplate)}
            disabled={installedTemplates.has(selectedTemplate.name)}
            style={{
              padding: '10px 24px',
              backgroundColor: installedTemplates.has(selectedTemplate.name) ? '#2E7D32' : '#F7E9A0',
              color: installedTemplates.has(selectedTemplate.name) ? '#FFF' : '#000',
              border: 'none',
              borderRadius: '8px',
              cursor: installedTemplates.has(selectedTemplate.name) ? 'default' : 'pointer',
              fontWeight: 600,
              fontSize: '14px',
              whiteSpace: 'nowrap',
            }}
          >
            {installedTemplates.has(selectedTemplate.name) ? '✓ Installed' : 'Install Template'}
          </button>
        </div>

        {/* Form preview */}
        <div style={{
          backgroundColor: '#113F67',
          border: '1px solid #6CA6C1',
          borderRadius: '8px',
          padding: '24px',
          maxHeight: '65vh',
          overflowY: 'auto',
        }}>
          <h3 style={{ color: '#F7E9A0', fontSize: '16px', margin: '0 0 16px 0' }}>Form Preview</h3>
          {selectedTemplate.elements.map((el, idx) => (
            <FormElementPreview key={el.id || idx} element={el} />
          ))}
        </div>
      </div>
    );
  }

  // Library view
  return (
    <div style={{ padding: '32px' }}>
      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#FFFFFF', margin: '0 0 8px 0' }}>
            Document Library
          </h1>
          <p style={{ fontSize: '14px', color: '#87CEEB', margin: 0 }}>
            Clinical form templates for client charts. Install templates to make them assignable to clients.
          </p>
        </div>
        <button
          onClick={handleInstallAll}
          disabled={installedCount === CLINICAL_FORM_TEMPLATES.length}
          style={{
            padding: '10px 24px',
            backgroundColor: installedCount === CLINICAL_FORM_TEMPLATES.length ? '#2E7D32' : '#F7E9A0',
            color: installedCount === CLINICAL_FORM_TEMPLATES.length ? '#FFF' : '#000',
            border: 'none',
            borderRadius: '8px',
            cursor: installedCount === CLINICAL_FORM_TEMPLATES.length ? 'default' : 'pointer',
            fontWeight: 600,
            fontSize: '14px',
            whiteSpace: 'nowrap',
          }}
        >
          {installedCount === CLINICAL_FORM_TEMPLATES.length ? '✓ All Installed' : `Install All (${CLINICAL_FORM_TEMPLATES.length - installedCount} remaining)`}
        </button>
      </div>

      {/* Success toast */}
      {showInstallSuccess && (
        <div style={{
          backgroundColor: '#2E7D32',
          color: '#FFF',
          padding: '12px 20px',
          borderRadius: '8px',
          marginBottom: '16px',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          Installed: {showInstallSuccess}
        </div>
      )}

      {/* Stats bar */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '24px',
      }}>
        <div style={{
          backgroundColor: '#113F67',
          border: '1px solid #6CA6C1',
          borderRadius: '8px',
          padding: '16px 24px',
          flex: 1,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#F7E9A0' }}>{CLINICAL_FORM_TEMPLATES.length}</div>
          <div style={{ fontSize: '12px', color: '#87CEEB', marginTop: '4px' }}>Available Templates</div>
        </div>
        <div style={{
          backgroundColor: '#113F67',
          border: '1px solid #6CA6C1',
          borderRadius: '8px',
          padding: '16px 24px',
          flex: 1,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#81C784' }}>{installedCount}</div>
          <div style={{ fontSize: '12px', color: '#87CEEB', marginTop: '4px' }}>Installed</div>
        </div>
        <div style={{
          backgroundColor: '#113F67',
          border: '1px solid #6CA6C1',
          borderRadius: '8px',
          padding: '16px 24px',
          flex: 1,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#FFB74D' }}>{CLINICAL_FORM_TEMPLATES.length - installedCount}</div>
          <div style={{ fontSize: '12px', color: '#87CEEB', marginTop: '4px' }}>Not Installed</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search templates..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{
            flex: '1 1 250px',
            padding: '10px 16px',
            backgroundColor: '#0D2137',
            border: '1px solid #6CA6C1',
            borderRadius: '8px',
            color: '#FFFFFF',
            fontSize: '14px',
            outline: 'none',
          }}
        />
        <div style={{ display: 'flex', gap: '4px' }}>
          {(['all', 'intake', 'assessment', 'consent', 'treatment-plan', 'hr'] as CategoryFilter[]).map(cat => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(cat)}
              style={{
                padding: '8px 16px',
                backgroundColor: categoryFilter === cat ? '#6CA6C1' : 'transparent',
                color: categoryFilter === cat ? '#000' : '#87CEEB',
                border: `1px solid ${categoryFilter === cat ? '#6CA6C1' : '#3A5F7A'}`,
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: categoryFilter === cat ? 600 : 400,
              }}
            >
              {cat === 'all' ? 'All' : CATEGORY_LABELS[cat]}
            </button>
          ))}
        </div>
      </div>

      {/* Template cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' }}>
        {filteredTemplates.map(template => {
          const installed = installedTemplates.has(template.name);
          return (
            <div
              key={template.name}
              style={{
                backgroundColor: '#113F67',
                border: `1px solid ${installed ? '#81C784' : '#6CA6C1'}`,
                borderRadius: '12px',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                transition: 'border-color 0.2s',
              }}
            >
              {/* Card header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#FFFFFF', margin: 0, flex: 1 }}>
                  {template.name}
                </h3>
                {installed && (
                  <span style={{
                    backgroundColor: '#2E7D32',
                    color: '#FFF',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontSize: '11px',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    marginLeft: '8px',
                  }}>
                    Installed
                  </span>
                )}
              </div>

              {/* Category + tags */}
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <span style={{
                  backgroundColor: CATEGORY_COLORS[template.category] || '#6CA6C1',
                  color: '#000',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '11px',
                  fontWeight: 600,
                }}>
                  {CATEGORY_LABELS[template.category]}
                </span>
                {template.medicaidRequired && (
                  <span style={{
                    backgroundColor: '#FF7043',
                    color: '#FFF',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontSize: '11px',
                    fontWeight: 600,
                  }}>
                    Medicaid
                  </span>
                )}
                <span style={{ color: '#87CEEB', fontSize: '11px', alignSelf: 'center' }}>
                  {template.elements.length} fields
                </span>
              </div>

              {/* Description */}
              <p style={{
                color: '#B0BEC5',
                fontSize: '13px',
                margin: 0,
                lineHeight: '1.4',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}>
                {template.description}
              </p>

              {/* Tags */}
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {template.tags.slice(0, 4).map(tag => (
                  <span key={tag} style={{
                    backgroundColor: '#0D2137',
                    color: '#6CA6C1',
                    padding: '2px 8px',
                    borderRadius: '8px',
                    fontSize: '11px',
                  }}>
                    {tag}
                  </span>
                ))}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '8px', marginTop: 'auto', paddingTop: '4px' }}>
                <button
                  onClick={() => handlePreview(template)}
                  style={{
                    flex: 1,
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    color: '#87CEEB',
                    border: '1px solid #6CA6C1',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                >
                  Preview
                </button>
                <button
                  onClick={() => handleInstall(template)}
                  disabled={installed}
                  style={{
                    flex: 1,
                    padding: '8px 16px',
                    backgroundColor: installed ? '#2E7D32' : '#F7E9A0',
                    color: installed ? '#FFF' : '#000',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: installed ? 'default' : 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                  }}
                >
                  {installed ? 'Installed' : 'Install'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredTemplates.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '48px',
          color: '#87CEEB',
          fontSize: '14px',
        }}>
          No templates match your search or filter.
        </div>
      )}
    </div>
  );
};

// =============================================
// Form Element Preview Component
// =============================================
const FormElementPreview: React.FC<{ element: FormFieldElement }> = ({ element }) => {
  const { type, content } = element;

  const fieldStyle: React.CSSProperties = {
    marginBottom: '16px',
    padding: '0',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '13px',
    fontWeight: 600,
    color: '#FFFFFF',
    marginBottom: '6px',
  };

  const inputPreviewStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    backgroundColor: '#0D2137',
    border: '1px solid #3A5F7A',
    borderRadius: '6px',
    color: '#6CA6C1',
    fontSize: '13px',
    boxSizing: 'border-box' as const,
  };

  switch (type) {
    case 'heading':
      return (
        <div style={{ ...fieldStyle, marginTop: '20px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#F7E9A0', margin: '0 0 4px 0', borderBottom: '1px solid #3A5F7A', paddingBottom: '8px' }}>
            {content.title}
          </h3>
        </div>
      );

    case 'description':
      return (
        <div style={fieldStyle}>
          <p style={{ fontSize: '13px', color: '#B0BEC5', margin: 0, lineHeight: '1.5', fontStyle: 'italic' }}>
            {content.text}
          </p>
        </div>
      );

    case 'divider':
      return <hr style={{ border: 'none', borderTop: '1px solid #3A5F7A', margin: '16px 0' }} />;

    case 'short-response':
      return (
        <div style={fieldStyle}>
          <div style={labelStyle}>
            {content.question} {content.required && <span style={{ color: '#FF7043' }}>*</span>}
          </div>
          <div style={inputPreviewStyle}>{content.placeholder || 'Short text response'}</div>
        </div>
      );

    case 'long-response':
      return (
        <div style={fieldStyle}>
          <div style={labelStyle}>
            {content.question} {content.required && <span style={{ color: '#FF7043' }}>*</span>}
          </div>
          <div style={{ ...inputPreviewStyle, minHeight: '60px' }}>{content.placeholder || 'Long text response'}</div>
        </div>
      );

    case 'single-option':
      return (
        <div style={fieldStyle}>
          <div style={labelStyle}>
            {content.question} {content.required && <span style={{ color: '#FF7043' }}>*</span>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {content.options?.map((opt, i) => (
              <label key={i} style={{ color: '#B0BEC5', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ width: '14px', height: '14px', borderRadius: '50%', border: '2px solid #6CA6C1', display: 'inline-block', flexShrink: 0 }} />
                {opt}
              </label>
            ))}
          </div>
        </div>
      );

    case 'multiple-options':
      return (
        <div style={fieldStyle}>
          <div style={labelStyle}>
            {content.question} {content.required && <span style={{ color: '#FF7043' }}>*</span>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {content.options?.map((opt, i) => (
              <label key={i} style={{ color: '#B0BEC5', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ width: '14px', height: '14px', borderRadius: '2px', border: '2px solid #6CA6C1', display: 'inline-block', flexShrink: 0 }} />
                {opt}
                {content.textResponses?.[i] && (
                  <span style={{ ...inputPreviewStyle, flex: 1, padding: '4px 8px', fontSize: '12px' }}>text field</span>
                )}
              </label>
            ))}
          </div>
        </div>
      );

    case 'dropdown':
      return (
        <div style={fieldStyle}>
          <div style={labelStyle}>
            {content.question} {content.required && <span style={{ color: '#FF7043' }}>*</span>}
          </div>
          <div style={inputPreviewStyle}>
            Select: {content.options?.join(', ')}
          </div>
        </div>
      );

    case 'date':
      return (
        <div style={fieldStyle}>
          <div style={labelStyle}>
            {content.question} {content.required && <span style={{ color: '#FF7043' }}>*</span>}
          </div>
          <div style={inputPreviewStyle}>MM/DD/YYYY</div>
        </div>
      );

    case 'signature':
      return (
        <div style={fieldStyle}>
          <div style={labelStyle}>
            {content.question} {content.required && <span style={{ color: '#FF7043' }}>*</span>}
          </div>
          <div style={{
            ...inputPreviewStyle,
            minHeight: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontStyle: 'italic',
          }}>
            Signature field
          </div>
        </div>
      );

    case 'group':
      return (
        <div style={{ ...fieldStyle, borderLeft: '3px solid #6CA6C1', paddingLeft: '16px' }}>
          <div style={labelStyle}>{content.question}</div>
        </div>
      );

    default:
      return null;
  }
};

export default Documentation;
